import React, { useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";

export default function LoginPage() {
  const { login, checkAuth } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState("main"); // main | email-login | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      return toast.show({ variant: "error", title: "Missing info", description: "Enter both email and password." });
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/email-login`, { email: email.trim(), password }, { withCredentials: true });
      await checkAuth();
      toast.show({ variant: "success", title: "Welcome back!" });
    } catch (err) {
      toast.show({
        variant: "error",
        title: "Login failed",
        description: err.response?.data?.detail || "Invalid email or password",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name.trim() || !emailValid(email)) {
      return toast.show({ variant: "error", title: "Check details", description: "Provide a name and valid email." });
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/signup`, { name: name.trim(), email: email.trim() });
      toast.show({
        variant: "success",
        title: "Account created!",
        description: "Check your email for your password (also check spam).",
        duration: 6000,
      });
      setMode("email-login");
      setName("");
      setPassword("");
    } catch (err) {
      toast.show({
        variant: "error",
        title: "Signup failed",
        description: err.response?.data?.detail || "Try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!emailValid(forgotEmail)) {
      return toast.show({ variant: "error", title: "Invalid email", description: "Please enter a valid email." });
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      toast.show({
        variant: "success",
        title: "Password reset sent",
        description: "If the email exists, a new password is on its way.",
        duration: 6000,
      });
      setForgotEmail("");
      setMode("email-login");
    } catch (err) {
      toast.show({ variant: "error", title: "Failed", description: err.response?.data?.detail || "Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900 text-slate-100">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl blob"></div>
        <div className="absolute bottom-0 -right-24 w-[28rem] h-[28rem] bg-violet-600/20 rounded-full blur-3xl blob" style={{ animationDelay: "5s" }}></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl blob" style={{ animationDelay: "9s" }}></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-10 safe-top safe-bottom">
        <div className="w-full max-w-md fade-in">
          {mode === "main" && (
            <MainPanel onGoogle={login} goEmail={() => setMode("email-login")} goSignup={() => setMode("signup")} />
          )}

          {mode === "email-login" && (
            <FormPanel
              testId="email-login-form"
              back={() => setMode("main")}
              icon="fa-envelope"
              iconColor="text-blue-400 bg-blue-500/15"
              title="Sign in with email"
              subtitle="Use the password sent to your email"
              footer={
                <p className="text-slate-400">
                  New here?{" "}
                  <button data-testid="go-signup-btn" onClick={() => setMode("signup")} className="text-blue-400 font-semibold hover:underline">
                    Create an account
                  </button>
                </p>
              }
            >
              <form onSubmit={handleEmailLogin} className="space-y-3">
                <Field icon="fa-envelope" testId="email-input">
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
                  />
                </Field>
                <Field icon="fa-lock" testId="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="text-slate-500 hover:text-slate-300"
                    aria-label="Toggle password"
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </Field>
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setForgotEmail(email); setMode("forgot"); }} className="text-amber-400 text-sm hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
                <SubmitBtn loading={submitting} testId="email-login-submit">
                  Sign in
                </SubmitBtn>
              </form>
            </FormPanel>
          )}

          {mode === "signup" && (
            <FormPanel
              testId="signup-form"
              back={() => setMode("main")}
              icon="fa-user-plus"
              iconColor="text-emerald-400 bg-emerald-500/15"
              title="Create your account"
              subtitle="We'll email your auto-generated password"
              footer={
                <p className="text-slate-400">
                  Already have an account?{" "}
                  <button onClick={() => setMode("email-login")} className="text-blue-400 font-semibold hover:underline">
                    Sign in
                  </button>
                </p>
              }
            >
              <form onSubmit={handleSignup} className="space-y-3">
                <Field icon="fa-user" testId="signup-name-input">
                  <input
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
                  />
                </Field>
                <Field icon="fa-envelope" testId="signup-email-input">
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
                  />
                </Field>
                <InfoBox color="blue" icon="fa-circle-info">
                  Your password will be emailed to you instantly. Please also check your spam folder.
                </InfoBox>
                <SubmitBtn loading={submitting} color="emerald" testId="signup-submit">
                  Create account
                </SubmitBtn>
              </form>
            </FormPanel>
          )}

          {mode === "forgot" && (
            <FormPanel
              testId="forgot-form"
              back={() => setMode("email-login")}
              icon="fa-key"
              iconColor="text-amber-400 bg-amber-500/15"
              title="Forgot password"
              subtitle="We'll email you a new one"
              footer={
                <p className="text-slate-400">
                  Remembered?{" "}
                  <button onClick={() => setMode("email-login")} className="text-blue-400 font-semibold hover:underline">
                    Sign in
                  </button>
                </p>
              }
            >
              <form onSubmit={handleForgot} className="space-y-3">
                <Field icon="fa-envelope" testId="forgot-email-input">
                  <input
                    type="email"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
                  />
                </Field>
                <InfoBox color="amber" icon="fa-triangle-exclamation">
                  A new password will be sent to your registered email. Check spam if needed.
                </InfoBox>
                <SubmitBtn loading={submitting} color="amber" testId="forgot-submit">
                  Send new password
                </SubmitBtn>
              </form>
            </FormPanel>
          )}
        </div>
      </div>
    </div>
  );
}

function MainPanel({ onGoogle, goEmail, goSignup }) {
  return (
    <div className="text-center fade-in">
      <div className="relative inline-flex w-28 h-28 rounded-3xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 items-center justify-center mb-6 ring-1 ring-white/10 shadow-2xl">
        <i className="fas fa-microphone-lines text-4xl bg-gradient-to-br from-blue-400 to-violet-400 bg-clip-text text-transparent"></i>
        <span className="absolute -bottom-1 -right-1 w-9 h-9 bg-emerald-500 rounded-full text-white text-xs font-bold flex items-center justify-center ring-4 ring-slate-900">
          <i className="fas fa-bolt"></i>
        </span>
      </div>
      <h1 className="text-4xl font-extrabold tracking-tight text-white mb-2">AusumLog</h1>
      <p className="text-slate-400 mb-8">Speak it. Save it. Summarize it.</p>

      <div className="grid grid-cols-3 gap-2 mb-8">
        <FeatureChip icon="fa-microphone" label="Voice → Text" />
        <FeatureChip icon="fa-wand-magic-sparkles" label="AI Summary" />
        <FeatureChip icon="fa-cloud-arrow-up" label="Sync & PWA" />
      </div>

      <button
        data-testid="google-signin-btn"
        onClick={onGoogle}
        className="w-full bg-white hover:bg-slate-100 text-slate-900 font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-3 btn-press shadow-xl"
      >
        <i className="fab fa-google text-lg" style={{ color: "#4285F4" }}></i>
        <span>Continue with Google</span>
      </button>

      <button
        data-testid="email-signin-btn"
        onClick={goEmail}
        className="mt-3 w-full bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-3 btn-press border border-slate-700"
      >
        <i className="fas fa-envelope"></i>
        <span>Sign in with Email</span>
      </button>

      <p className="text-slate-500 text-sm mt-6">
        New to AusumLog?{" "}
        <button data-testid="signup-link-main" onClick={goSignup} className="text-blue-400 font-semibold hover:underline">
          Create an account
        </button>
      </p>
    </div>
  );
}

function FeatureChip({ icon, label }) {
  return (
    <div className="rounded-xl bg-slate-800/60 border border-slate-700 px-3 py-3 flex flex-col items-center gap-1.5">
      <i className={`fas ${icon} text-blue-400`}></i>
      <span className="text-[11px] text-slate-300 font-medium">{label}</span>
    </div>
  );
}

function FormPanel({ children, back, icon, iconColor, title, subtitle, footer, testId }) {
  return (
    <div className="relative bg-slate-800/60 backdrop-blur-xl border border-slate-700 rounded-3xl p-6 sm:p-8 shadow-2xl fade-in" data-testid={testId}>
      <button
        onClick={back}
        data-testid="form-back-btn"
        className="absolute top-4 left-4 w-9 h-9 rounded-full hover:bg-slate-700/60 text-slate-300 flex items-center justify-center"
      >
        <i className="fas fa-arrow-left"></i>
      </button>
      <div className="text-center mb-6">
        <div className={`w-16 h-16 mx-auto rounded-2xl ${iconColor} flex items-center justify-center mb-4`}>
          <i className={`fas ${icon} text-2xl`}></i>
        </div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
      </div>
      {children}
      <div className="text-center text-sm mt-6">{footer}</div>
    </div>
  );
}

function Field({ icon, children, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 bg-slate-900/70 border border-slate-700 focus-within:border-blue-500 transition-colors rounded-xl px-4 py-3">
      <i className={`fas ${icon} text-slate-500 w-4`}></i>
      {children}
    </div>
  );
}

function InfoBox({ children, color = "blue", icon }) {
  const map = {
    blue: "bg-blue-500/10 text-blue-200 border-blue-500/30",
    amber: "bg-amber-500/10 text-amber-200 border-amber-500/30",
  };
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${map[color]}`}>
      <i className={`fas ${icon} mt-0.5`}></i>
      <span className="flex-1">{children}</span>
    </div>
  );
}

function SubmitBtn({ loading, children, color = "blue", testId }) {
  const map = {
    blue: "bg-blue-600 hover:bg-blue-500",
    emerald: "bg-emerald-600 hover:bg-emerald-500",
    amber: "bg-amber-500 hover:bg-amber-400",
  };
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testId}
      className={`w-full ${map[color]} text-white font-semibold py-3.5 rounded-xl btn-press flex items-center justify-center gap-2 disabled:opacity-60`}
    >
      {loading ? <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full spin"></span> : children}
    </button>
  );
}
